/**
 * Followup Query Parser
 *
 * Parses XML tags in the format: <followup query="..." />
 * Each tag typically appears on its own line in assistant messages.
 *
 * Processing order in the rendering pipeline:
 * 1. Charts are preprocessed first (chart tags)
 * 2. Followup queries are processed second (this module)
 * 3. Final markdown rendering happens last
 */

export interface FollowupQuery {
  original: string;   // Raw XML-escaped text from the tag
  decoded: string;    // Full decoded text (sent to backend)
  display: string;    // Truncated text (shown on button, max 50 chars)
}

const MAX_DISPLAY_LENGTH = 50;

/**
 * Decodes XML entities using browser's native DOMParser.
 * Handles all standard entities: &quot;, &amp;, &lt;, &gt;, &apos;, &#39;
 */
export function decodeXMLEntities(text: string): string {
  try {
    const parser = new DOMParser();
    const dom = parser.parseFromString(
      `<!DOCTYPE html><html><body>${text}</body></html>`,
      'text/html'
    );
    return dom.body.textContent || text;
  } catch (error) {
    console.error('[followup-parser] XML decode error:', error);
    return text; // Return original on error
  }
}

/**
 * Strips all followup tags from content during streaming.
 * Used to prevent raw XML from flashing on screen.
 */
export function stripFollowupTags(content: string): string {
  return content.replace(/<followup\s+query="[^"]*"\s*\/>/g, '');
}

/**
 * Parses followup query tags from completed assistant messages.
 * Returns both the parsed queries and the cleaned content (tags removed).
 */
export function parseFollowupQueries(content: string): {
  queries: FollowupQuery[];
  cleanedContent: string;
} {
  const queries: FollowupQuery[] = [];
  const FOLLOWUP_TAG_REGEX = /<followup\s+query="([^"]*)"\s*\/>/g;

  let match;
  const seenQueries = new Set<string>();

  try {
    // Extract all followup tags
    while ((match = FOLLOWUP_TAG_REGEX.exec(content)) !== null) {
      const original = match[1]; // Raw XML-escaped text

      // Decode XML entities
      const decoded = decodeXMLEntities(original);

      // Skip empty or whitespace-only queries
      if (!decoded.trim()) {
        continue;
      }

      // Deduplicate based on decoded text
      if (seenQueries.has(decoded)) {
        continue;
      }
      seenQueries.add(decoded);

      // Truncate for display if needed
      const display = decoded.length > MAX_DISPLAY_LENGTH
        ? decoded.substring(0, MAX_DISPLAY_LENGTH) + '...'
        : decoded;

      queries.push({
        original,
        decoded,
        display,
      });
    }
  } catch (error) {
    console.error('[followup-parser] Error parsing followup queries:', error);
  }

  // Remove all followup tags from content
  const cleanedContent = content.replace(FOLLOWUP_TAG_REGEX, '');

  return {
    queries,
    cleanedContent,
  };
}
